import { useEffect, useRef, useState } from 'react'
import { FileText, Upload } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useCalculatorStore } from '@/store/calculatorStore'
import { importReportCard } from '@/lib/reportCardImport'
import {
  prepareReportCardFile,
  type PreparedReportCardFile,
} from '@/lib/reportCardPreparation'
import type {
  ReportCardImportMatch,
  ReportCardImportResponse,
  ReportCardImportUnmatched,
} from '@/lib/reportCardImport'
import ImportDropzone from '@/components/import-report-card/ImportDropzone'
import ImportReviewCard from '@/components/import-report-card/ImportReviewCard'

type DraftImport = ReportCardImportMatch

const ImportReportCard = () => {
  const allCourses = useCalculatorStore((state) => state.courses)
  const applyImportedCourses = useCalculatorStore(
    (state) => state.applyImportedCourses
  )
  const hasExistingImprovementGrades = allCourses.some(
    (course) => course.improved > 0 || course.improved2 > 0
  )

  const [isOpen, setIsOpen] = useState(false)
  const [preparedFile, setPreparedFile] = useState<PreparedReportCardFile | null>(
    null
  )
  const [isPreparingFile, setIsPreparingFile] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ReportCardImportResponse | null>(null)
  const [draftImports, setDraftImports] = useState<DraftImport[]>([])
  const [unmatched, setUnmatched] = useState<ReportCardImportUnmatched[]>([])
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const preparationRequestIdRef = useRef(0)
  const resultScrollContainerRef = useRef<HTMLDivElement>(null)

  const courseCatalog = allCourses.map((course) => ({
    id: course.id,
    name: course.name,
  }))

  useEffect(() => {
    const scrollElement = resultScrollContainerRef.current
    if (!scrollElement) return

    const handleWheel = (event: WheelEvent) => {
      if (
        scrollElement.contains(event.target as Node) &&
        scrollElement.scrollHeight > scrollElement.clientHeight
      ) {
        event.preventDefault()
        scrollElement.scrollTop += event.deltaY
      }
    }

    const handleTouchMove = (event: TouchEvent) => {
      if (
        scrollElement.contains(event.target as Node) &&
        scrollElement.scrollHeight > scrollElement.clientHeight
      ) {
        event.stopPropagation()
      }
    }

    scrollElement.addEventListener('wheel', handleWheel, { passive: false })
    scrollElement.addEventListener('touchmove', handleTouchMove, {
      passive: true,
    })

    return () => {
      scrollElement.removeEventListener('wheel', handleWheel)
      scrollElement.removeEventListener('touchmove', handleTouchMove)
    }
  }, [result])

  const resetState = () => {
    preparationRequestIdRef.current += 1
    setPreparedFile(null)
    setIsPreparingFile(false)
    setIsImporting(false)
    setError(null)
    setResult(null)
    setDraftImports([])
    setUnmatched([])
    setHasAcceptedTerms(false)
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      resetState()
    }
  }

  const handleAnalyze = async () => {
    if (!preparedFile) {
      setError('Velg en PDF eller et bilde først.')
      return
    }

    if (!hasAcceptedTerms) {
      setError('Du må godta vilkårene før vi kan lese dokumentet.')
      return
    }

    const requestId = preparationRequestIdRef.current + 1
    preparationRequestIdRef.current = requestId
    setIsImporting(true)
    setError(null)

    try {
      const response = await importReportCard(preparedFile.uploadFile, courseCatalog)
      if (preparationRequestIdRef.current !== requestId) {
        return
      }

      setResult(response)
      setDraftImports(response.imports)
      setUnmatched(response.unmatched)
    } catch (importError) {
      if (preparationRequestIdRef.current !== requestId) {
        return
      }

      setError(
        importError instanceof Error
          ? importError.message
          : 'Kunne ikke lese dokumentet.'
      )
    } finally {
      if (preparationRequestIdRef.current !== requestId) {
        return
      }

      setIsImporting(false)
    }
  }

  const updateDraftImport = (
    index: number,
    updates: Partial<Pick<DraftImport, 'grade' | 'exam' | 'exam2'>>
  ) => {
    setDraftImports((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...updates } : item
      )
    )
  }

  const removeDraftImport = (index: number) => {
    setDraftImports((current) =>
      current.filter((_, itemIndex) => itemIndex !== index)
    )
  }

  const handleApply = () => {
    applyImportedCourses(
      draftImports.map((item) => ({
        courseId: item.matchedCourseId,
        grade: item.grade,
        exam: item.exam,
        exam2: item.exam2,
      }))
    )
    handleOpenChange(false)
  }

  const handleSelectedFile = (selectedFile: File | null) => {
    const requestId = preparationRequestIdRef.current + 1
    preparationRequestIdRef.current = requestId

    if (!selectedFile) {
      setPreparedFile(null)
      setIsPreparingFile(false)
      setError(null)
      return
    }

    setPreparedFile(null)
    setIsPreparingFile(true)
    setError(null)

    void prepareReportCardFile(selectedFile)
      .then((nextPreparedFile) => {
        if (preparationRequestIdRef.current !== requestId) {
          return
        }

        setPreparedFile(nextPreparedFile)
      })
      .catch((preparationError) => {
        if (preparationRequestIdRef.current !== requestId) {
          return
        }

        setPreparedFile(null)
        setError(
          preparationError instanceof Error
            ? preparationError.message
            : 'Kunne ikke klargjøre fila.'
        )
      })
      .finally(() => {
        if (preparationRequestIdRef.current !== requestId) {
          return
        }

        setIsPreparingFile(false)
      })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button className="w-full text-sm text-center cursor-pointer text-purple-800 bg-purple-100 px-4 py-3 rounded-md hover:bg-purple-200 flex items-center justify-center gap-2">
          <Upload className="size-4" />
          Last opp vitnemål eller karakterutskrift
        </button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-[780px]">
        <DialogHeader className="p-5 pb-3">
          <DialogTitle className="text-left text-base sm:text-lg">
            Last opp vitnemål eller karakterutskrift
          </DialogTitle>
          <DialogDescription className="text-left text-xs sm:text-sm">
            Last opp PDF eller bilde. Du får se og justere forslagene før de
            legges inn.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={resultScrollContainerRef}
          className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5 space-y-4 touch-pan-y [-webkit-overflow-scrolling:touch]"
        >
          {result ? (
            <div className="rounded-xl border border-warm-gray-300 bg-warm-gray-100/80 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
                  <FileText className="size-4" />
                  <span className="max-w-[18rem] truncate">
                    {preparedFile?.displayFile.name ?? 'Valgt fil'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    preparationRequestIdRef.current += 1
                    setResult(null)
                    setDraftImports([])
                    setUnmatched([])
                    setError(null)
                    setPreparedFile(null)
                  }}
                  className="rounded-md border border-warm-gray-400 bg-white px-4 py-2 text-sm cursor-pointer hover:bg-warm-gray-100"
                >
                  Start på nytt
                </button>
              </div>
            </div>
          ) : (
            <ImportDropzone
              preparedFile={preparedFile}
              isPreparingFile={isPreparingFile}
              isImporting={isImporting}
              error={error}
              hasAcceptedTerms={hasAcceptedTerms}
              onAcceptedTermsChange={setHasAcceptedTerms}
              onSelectedFile={handleSelectedFile}
              onAnalyze={handleAnalyze}
            />
          )}

          {result && (
            <>
              {result.warnings.length > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <div className="font-semibold">Viktig å sjekke</div>
                  <ul className="mt-2 space-y-1">
                    {result.warnings.map((warning, index) => (
                      <li key={`${index}-${warning}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold sm:text-base">
                  Forslag som blir lagt inn
                </h3>
                <p className="mt-1 text-xs text-warm-gray-800 sm:text-sm">
                  Sjekk fag og karakterer før du godkjenner.
                </p>
                <p className="mt-1 text-xs text-warm-gray-800 sm:text-sm">
                  Når du bruker disse karakterene, erstatter de karakterene som
                  allerede ligger i kalkulatoren.
                </p>
                {hasExistingImprovementGrades && (
                  <p className="mt-1 text-xs text-amber-900 sm:text-sm">
                    Du har allerede lagt inn forbedringskarakterer. De blir også
                    erstattet når du bruker importen.
                  </p>
                )}
              </div>

              {draftImports.length === 0 ? (
                <div className="rounded-lg border border-warm-gray-300 bg-warm-gray-100 p-4 text-sm text-warm-gray-800">
                  Fant ingen fag som kunne matches sikkert mot faglisten.
                </div>
              ) : (
                <div className="space-y-3">
                  {draftImports.map((item, index) => (
                    <ImportReviewCard
                      key={`${item.matchedCourseId}-${index}`}
                      item={item}
                      onUpdate={(updates) => updateDraftImport(index, updates)}
                      onRemove={() => removeDraftImport(index)}
                    />
                  ))}
                </div>
              )}

              {unmatched.length > 0 && (
                <div className="rounded-lg border border-warm-gray-300 bg-warm-gray-100 p-4">
                  <h3 className="text-sm font-semibold sm:text-base">
                    Fag som ikke ble matchet automatisk
                  </h3>
                  <div className="mt-2 space-y-2 text-xs text-warm-gray-800 sm:text-sm">
                    {unmatched.map((item) => (
                      <div key={`${item.rawCourseName}-${item.notes}`}>
                        <div className="font-medium">{item.rawCourseName}</div>
                        <div>{item.notes}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => handleOpenChange(false)}
                  className="rounded-md border border-warm-gray-400 px-4 py-2 text-sm cursor-pointer hover:bg-warm-gray-100"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={draftImports.length === 0}
                  className="rounded-md bg-purple-700 px-4 py-2 text-sm font-medium text-white cursor-pointer hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  Bruk disse karakterene
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ImportReportCard
